/**
 * TBMuktBharat Campaign List Page
 * Uses real API with fallback to mock data
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Calendar, Users, MapPin, Target, TrendingUp, Search, Heart, Share2, Loader2, Pencil, Trash2 } from 'lucide-react'
import campaignService, { Campaign } from '@/services/campaignService'
import { useAuthStore } from '@/stores/authStore'
import CampaignFormModal from '@/components/campaign/CampaignFormModal'

// ================================================================================
// MOCK DATA - Sample campaigns for display when API returns empty
// ================================================================================
const MOCK_CAMPAIGNS: Campaign[] = [
  {
    id: 1,
    title: 'TB-Free Village Initiative',
    description: 'Comprehensive TB screening and awareness campaign across 50 villages in Maharashtra. This initiative aims to provide free TB screening, awareness programs, and support for patients in rural areas.',
    type: 'SCREENING',
    status: 'ACTIVE',
    startDate: '2026-01-01',
    endDate: '2026-03-31',
    targetAmount: 500000,
    collectedAmount: 325000,
    targetParticipants: 10000,
    actualParticipants: 6500,
    location: 'Maharashtra',
    isFeatured: true
  },
  {
    id: 2,
    title: 'Awareness Week 2026',
    description: 'National TB awareness week with educational programs, free screenings, and community events across India.',
    type: 'AWARENESS',
    status: 'ACTIVE',
    startDate: '2026-02-01',
    endDate: '2026-02-07',
    targetParticipants: 50000,
    actualParticipants: 32000,
    location: 'Pan India',
    isFeatured: true
  },
  {
    id: 3,
    title: 'Health Camp for Workers',
    description: 'Free TB screening camp for factory and construction workers in Delhi NCR region.',
    type: 'HEALTH_CAMP',
    status: 'ACTIVE',
    startDate: '2026-01-15',
    endDate: '2026-02-15',
    targetAmount: 200000,
    collectedAmount: 180000,
    targetParticipants: 5000,
    actualParticipants: 4500,
    location: 'Delhi NCR',
    isFeatured: false
  },
  {
    id: 4,
    title: 'Support a TB Patient',
    description: 'Provide nutritional support and medicines for TB patients undergoing treatment across India.',
    type: 'SUPPORT',
    status: 'ACTIVE',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    targetAmount: 1000000,
    collectedAmount: 750000,
    targetParticipants: 1000,
    actualParticipants: 750,
    location: 'All India',
    isFeatured: false
  },
  {
    id: 5,
    title: 'Vaccination Drive',
    description: 'TB vaccination drive for high-risk populations in urban slums.',
    type: 'VACCINATION',
    status: 'ACTIVE',
    startDate: '2026-03-01',
    endDate: '2026-04-30',
    targetParticipants: 25000,
    actualParticipants: 18000,
    location: 'Mumbai, Pune, Nagpur',
    isFeatured: true
  },
  {
    id: 6,
    title: 'Community Outreach Program',
    description: 'Door-to-door awareness campaign in rural areas to identify undiagnosed TB cases.',
    type: 'COMMUNITY_OUTREACH',
    status: 'ACTIVE',
    startDate: '2026-02-15',
    endDate: '2026-05-15',
    targetParticipants: 15000,
    actualParticipants: 8500,
    location: 'Rajasthan',
    isFeatured: false
  }
]

// ================================================================================
// TYPE COLORS - Static lookup for performance
// ================================================================================
const TYPE_COLORS: Record<string, string> = {
  AWARENESS: 'bg-blue-100 text-blue-700',
  SCREENING: 'bg-green-100 text-green-700',
  HEALTH_CAMP: 'bg-amber-100 text-amber-700',
  SUPPORT: 'bg-purple-100 text-purple-700',
  VACCINATION: 'bg-red-100 text-red-700',
  COMMUNITY_OUTREACH: 'bg-gray-100 text-gray-700',
  EDUCATION: 'bg-cyan-100 text-cyan-700'
}

// ================================================================================
// CAMPAIGN CARD COMPONENT - Memoized for performance
// ================================================================================
const CampaignCard = React.memo(({ campaign }: { campaign: Campaign }) => {
  const progress = useMemo(() => {
    return campaign.targetAmount
      ? Math.round(((campaign.collectedAmount || 0) / campaign.targetAmount) * 100)
      : campaign.targetParticipants
        ? Math.round(((campaign.actualParticipants || 0) / campaign.targetParticipants) * 100)
        : 0
  }, [campaign])

  const typeColor = TYPE_COLORS[campaign.type] || 'bg-gray-100 text-gray-700'
  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
  }

  return (
    <GovCard className="overflow-hidden p-0 group hover:shadow-md transition-shadow">
      {/* Image Placeholder */}
      <div className="relative h-40 bg-gradient-to-br from-blue-500 to-cyan-500">
        <div className="absolute inset-0 flex items-center justify-center">
          <Target className="w-12 h-12 text-white/30" />
        </div>
        {campaign.isFeatured && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white">
            Featured
          </span>
        )}
        <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-medium rounded-full ${typeColor}`}>
          {campaign.type?.replace('_', ' ') || 'CAMPAIGN'}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
          {campaign.title}
        </h3>
        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{campaign.description}</p>

        {/* Meta Info */}
        <div className="flex flex-wrap gap-2 mb-3 text-xs text-gray-500">
          {campaign.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {campaign.location}
            </span>
          )}
          {campaign.startDate && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(campaign.startDate)} - {formatDate(campaign.endDate || '')}
            </span>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500">Progress</span>
            <span className="font-medium text-green-600">{progress}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {campaign.targetAmount && (
            <div>
              <div className="text-sm font-bold text-gray-900">
                ₹{((campaign.collectedAmount || 0) / 100000).toFixed(1)}L
              </div>
              <div className="text-xs text-gray-500">raised of ₹{(campaign.targetAmount / 100000).toFixed(1)}L</div>
            </div>
          )}
          <div>
            <div className="text-sm font-bold text-gray-900">
              {(campaign.actualParticipants || 0).toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">participants</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Link
            to={`/campaigns/${campaign.id}`}
            className="flex-1 text-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            View Details
          </Link>
          <button className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-lg transition-colors">
            <Heart className="w-4 h-4" />
          </button>
          <button className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-gray-100 rounded-lg transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </GovCard>
  )
})

CampaignCard.displayName = 'CampaignCard'

// ================================================================================
// STATS CARD COMPONENT - Memoized
// ================================================================================
const StatsCard = React.memo(({ icon: Icon, value, label, colorClass }: {
  icon: React.ComponentType<{ className?: string }>
  value: string | number
  label: string
  colorClass: string
}) => (
  <GovCard className="text-center py-4">
    <div className={`w-10 h-10 mx-auto mb-2 rounded-lg ${colorClass} flex items-center justify-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="text-xl font-bold text-gray-900">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </GovCard>
))

StatsCard.displayName = 'StatsCard'

// ================================================================================
// MAIN CAMPAIGN LIST PAGE
// ================================================================================
const CampaignListPage = () => {
  const { isAuthenticated } = useAuthStore()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [myCampaigns, setMyCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [myLoading, setMyLoading] = useState(false)
  const [publishingId, setPublishingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [view, setView] = useState<'all' | 'my'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [stats, setStats] = useState({ activeCampaigns: 0, totalParticipants: 0, totalCollected: 0 })

  // Fetch campaigns (public list — ACTIVE by default)
  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true)
      try {
        const response = await campaignService.getCampaigns({
          type: selectedType !== 'all' ? selectedType : undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          search: searchQuery || undefined,
          page: 0,
          size: 50
        })
        setCampaigns(response.content || [])
      } catch (error) {
        console.error('Failed to fetch campaigns:', error)
        setCampaigns([])
      } finally {
        setLoading(false)
      }
    }

    fetchCampaigns()
  }, [searchQuery, selectedType, selectedStatus])

  // Fetch the current user's own campaigns (all statuses, incl. DRAFT)
  const fetchMyCampaigns = useCallback(async () => {
    if (!isAuthenticated) return
    setMyLoading(true)
    try {
      const response = await campaignService.getMyCampaigns({ page: 0, size: 50 })
      setMyCampaigns(response.content || [])
    } catch (error) {
      console.error('Failed to fetch my campaigns:', error)
      setMyCampaigns([])
    } finally {
      setMyLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (view === 'my') fetchMyCampaigns()
  }, [view, fetchMyCampaigns])

  // Publish (activate) one of the user's own DRAFT campaigns
  const handlePublish = useCallback(async (id: number) => {
    setPublishingId(id)
    try {
      await campaignService.publishCampaign(id)
      await fetchMyCampaigns()
      // A newly-published campaign should now appear in the public list too
      const response = await campaignService.getCampaigns({ page: 0, size: 50 })
      setCampaigns(response.content || [])
    } catch (err: any) {
      console.error('Failed to publish campaign:', err)
      alert(err?.response?.data?.message || 'Failed to publish campaign')
    } finally {
      setPublishingId(null)
    }
  }, [fetchMyCampaigns])

  // Open the edit modal for one of the user's own campaigns
  const handleEdit = useCallback((campaign: Campaign) => {
    setEditingCampaign(campaign)
    setShowEditModal(true)
  }, [])

  // After a successful edit, refresh lists and close the modal
  const handleEditSuccess = useCallback(async () => {
    await fetchMyCampaigns()
    const response = await campaignService.getCampaigns({ page: 0, size: 50 })
    setCampaigns(response.content || [])
  }, [fetchMyCampaigns])

  // Delete one of the user's own campaigns (creator only; backend enforces)
  const handleDelete = useCallback(async (campaign: Campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.title}"? This cannot be undone.`)) return
    setDeletingId(campaign.id)
    try {
      await campaignService.deleteCampaign(campaign.id)
      await fetchMyCampaigns()
      const response = await campaignService.getCampaigns({ page: 0, size: 50 })
      setCampaigns(response.content || [])
    } catch (err: any) {
      console.error('Failed to delete campaign:', err)
      alert(err?.response?.data?.message || 'Failed to delete campaign')
    } finally {
      setDeletingId(null)
    }
  }, [fetchMyCampaigns])

  // Fetch stats (real values; no mock fallback)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statsData = await campaignService.getStats()
        setStats(statsData)
      } catch (error) {
        console.error('Failed to fetch stats:', error)
      }
    }
    fetchStats()
  }, [])

  // Filtered campaigns (client-side filtering as backup)
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(campaign => {
      const matchesSearch = !searchQuery ||
        campaign.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (campaign.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
      const matchesType = selectedType === 'all' || campaign.type === selectedType
      const matchesStatus = selectedStatus === 'all' || campaign.status === selectedStatus
      return matchesSearch && matchesType && matchesStatus
    })
  }, [campaigns, searchQuery, selectedType, selectedStatus])

  const campaignTypes = ['all', 'AWARENESS', 'SCREENING', 'HEALTH_CAMP', 'SUPPORT', 'VACCINATION', 'COMMUNITY_OUTREACH', 'EDUCATION']
  const statuses = ['all', 'ACTIVE', 'DRAFT', 'COMPLETED', 'CANCELLED']

  return (
    <GovLayout>
      {/* Page Title */}
      <GovPageTitle
        title="TB Campaigns"
        subtitle="Join the fight against tuberculosis"
        icon={Target}
      />

      {/* Hero Section */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Join the Fight Against TB
        </h1>
        <p className="text-sm text-gray-500 max-w-lg mx-auto">
          Participate in campaigns across India to raise awareness, support patients, and help eliminate tuberculosis.
        </p>
      </div>

      {/* CTA Section - Start Your Own Campaign (any logged-in user may create) */}
      <GovCard className="mb-6 text-center bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
        <h2 className="text-lg font-bold text-gray-900 mb-2">
          Start Your Own Campaign
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Have an idea to help fight TB in your community? Create a campaign and make a difference.
        </p>
        <Link to="/campaigns/create">
          <GovButton size="lg">Create Campaign</GovButton>
        </Link>
      </GovCard>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatsCard
          icon={Target}
          value={stats.activeCampaigns}
          label="Active"
          colorClass="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatsCard
          icon={Users}
          value={stats.totalParticipants.toLocaleString()}
          label="Participants"
          colorClass="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatsCard
          icon={TrendingUp}
          value={`₹${(stats.totalCollected / 100000).toFixed(1)}L`}
          label="Raised"
          colorClass="bg-gradient-to-br from-purple-500 to-purple-600"
        />
      </div>

      {/* View toggle: All campaigns vs My campaigns */}
      {isAuthenticated && (
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => setView('all')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              view === 'all' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All Campaigns
          </button>
          <button
            onClick={() => setView('my')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              view === 'my' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            My Campaigns
          </button>
        </div>
      )}

      {/* Search & Filters */}
      <GovCard className="mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {campaignTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type.replace('_', ' ')}
              </option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status}
              </option>
            ))}
          </select>
        </div>
      </GovCard>

      {/* Campaigns Grid */}
      {view === 'my' ? (
        // --- My Campaigns (current user's own, all statuses) ---
        myLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : myCampaigns.length === 0 ? (
          <GovEmptyState
            icon="🎯"
            title="You haven't created any campaigns"
            description="Start your own campaign to see it here."
          />
        ) : (
          <div className="space-y-3">
            {myCampaigns.map(campaign => (
              <GovCard key={campaign.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{campaign.title}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      campaign.status === 'ACTIVE' ? 'bg-green-100 text-green-700'
                      : campaign.status === 'DRAFT' ? 'bg-amber-100 text-amber-700'
                      : campaign.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                    }`}>
                      {campaign.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-1">{campaign.description || 'No description'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/campaigns/${campaign.id}`}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    View
                  </Link>
                  <GovButton size="sm" variant="secondary" onClick={() => handleEdit(campaign)}>
                    <Pencil className="w-3.5 h-3.5 mr-1 inline" /> Edit
                  </GovButton>
                  {campaign.status === 'DRAFT' && (
                    <GovButton
                      size="sm"
                      onClick={() => handlePublish(campaign.id)}
                      disabled={publishingId === campaign.id}
                    >
                      {publishingId === campaign.id ? 'Publishing…' : 'Publish'}
                    </GovButton>
                  )}
                  <GovButton
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(campaign)}
                    disabled={deletingId === campaign.id}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1 inline" />
                    {deletingId === campaign.id ? 'Deleting…' : 'Delete'}
                  </GovButton>
                </div>
              </GovCard>
            ))}
          </div>
        )
      ) : (
        // --- All campaigns (public list) ---
        loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : filteredCampaigns.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCampaigns.map(campaign => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        ) : (
          <GovEmptyState
            icon="🎯"
            title="No campaigns found"
            description="Try adjusting your search or filters"
          />
        )
      )}

      {/* Edit modal — creator-only (backend also enforces ownership) */}
      <CampaignFormModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        campaign={editingCampaign}
      />
    </GovLayout>
  )
}

export default React.memo(CampaignListPage)